import { Button, ConfirmDialog, Field, Message, TextOutput } from 'Components/Core';
import { ListFieldSection } from 'Components/Layout';
import { actions } from 'Data/ReduxViewModels/InterLab';
import { Severity } from 'Data/ViewModels/Core/Severity';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonGroup } from 'semantic-ui-react';
import { InterLabViewHistoryDialog } from '../Share';
import { SelectedSamplesTable } from './SelectedSamplesTable';

const InterLabCopy: React.FC = () => {
  const dispatch = useDispatch();

  const id = useSelector((state) => state.interLab.workOrder?.id);
  const newWorkOrderId = useSelector((state) => state.interLab.workOrder?.newWorkOrderId);
  const isClientGlobal = useSelector((state) => state.interLab.workOrder?.isClientGlobal);
  const isProjectGlobal = useSelector((state) => state.interLab.workOrder?.isProjectGlobal);
  const isNewWorkOrderIdTooLong = useSelector((state) => state.interLab.workOrder?.isNewWorkOrderIdTooLong);
  const containsUnsupportedSamples = useSelector((state) => state.interLab.workOrder?.containsUnsupportedSamples);
  const selectedSamples = useSelector(
    pipe(
      (state) => state.interLab.workOrder?.samples ?? [],
      (s) => s.filter((x) => x.checked)
    ),
    deepEquals
  );
  const isBusy = useSelector((state) => state.interLab.status.busy);

  const noSamplesSelected = selectedSamples.length === 0;
  const anySampleHasTooLongSubSampleId = selectedSamples?.some((x) => x.isSubSampleIdTooLong);

  const messages: { message: string; hasMessage?: boolean; severity?: Severity }[] = [
    {
      message:
        !isClientGlobal && !isProjectGlobal
          ? 'Clients and Projects must be global in scope in order to transfer work orders.'
          : !isClientGlobal
          ? 'Clients must be global in scope in order to transfer work orders.'
          : !isProjectGlobal
          ? 'Projects must be global in scope in order to transfer work orders.'
          : '',
      hasMessage: !isClientGlobal || !isProjectGlobal,
    },
    {
      message: 'Cannot copy work order until at least one sample is selected.',
      hasMessage: noSamplesSelected,
    },
    {
      message:
        'New work order ID is too long and will not fit in the available database field size. Please contact Spectra QEST.',
      hasMessage: isNewWorkOrderIdTooLong,
      severity: 'error',
    },
    {
      message:
        'Sub-sample ID is too long and will not fit in the available database field size. Please contact Spectra QEST.',
      hasMessage: anySampleHasTooLongSubSampleId,
      severity: 'error',
    },
    {
      message: 'Work order contains invalid samples. Only Aggregate/Soil, Asphalt, and Bitumen samples can be copied.',
      hasMessage: containsUnsupportedSamples,
      severity: 'error',
    },
  ];

  const copy = () => {
    dispatch(actions.copy({ workOrderId: id!, sampleIds: selectedSamples.map((x) => x.id) }));
  };

  return (
    <>
      <ListFieldSection>
        <Field input={<TextOutput />} value={newWorkOrderId} label="New Work Order ID" />
      </ListFieldSection>
      <ListFieldSection
        messages={
          !!newWorkOrderId
            ? messages.map(({ message, hasMessage, severity }) =>
                hasMessage ? { message: message, severity: severity ?? 'info' } : {}
              )
            : []
        }
      >
        <ButtonGroup>
          <ConfirmDialog
            confirmTitle="Confirm Copying of Samples"
            confirmButtonText="Copy"
            confirmCancelButtonText="Cancel"
            confirmText={() => (
              <>
                <Message border={false}>
                  This work order will be copied and given a new work order ID. The following samples will also be
                  copied and given new sub-sample IDs.
                </Message>
                <SelectedSamplesTable selectedSamples={selectedSamples} />
              </>
            )}
          >
            <Button
              primary
              disabled={
                anySampleHasTooLongSubSampleId ||
                isNewWorkOrderIdTooLong ||
                noSamplesSelected ||
                !isClientGlobal ||
                !isProjectGlobal ||
                containsUnsupportedSamples
              }
              onClick={copy}
              loading={isBusy}
            >
              Copy
            </Button>
          </ConfirmDialog>
          <InterLabViewHistoryDialog />
        </ButtonGroup>
      </ListFieldSection>
    </>
  );
};

export { InterLabCopy };
